import type {
  RelatedPublicSegment,
  RelatedPublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api/udm";
import type * as UDM from "@busbud/int-schemas/udm";
import type { Vehicle } from "@busbud/int-schemas/udm/properties/vehicle";

import { isNotNil } from "@app/helpers/is-not-nil";
import { getActiveFloorIndex, getSeatById } from "@app/helpers/udm/vehicle";
import type { SelectedSeats } from "@app/modules/results/hooks/navigation/decode-search-params";
import { getNormalizedClassNameCapitalized } from "@app/modules/results/utils/classes/normalized-class-name";
import type { SegmentSelectedSeats } from "@app/modules/seat-selection-first/store";
import type { TranslatorService } from "@app/services/translator";
import type { NapiV2CartSegment } from "@app/types/napi-v2-cart-types";

import type { SelectedSeatsQueryParam } from "../hooks/use-navigation";

export const getPassengersCountFromSelectedSeats = (
  selected_seats: SelectedSeatsQueryParam | undefined
) => {
  if (typeof selected_seats === "number") {
    return selected_seats;
  }

  if (selected_seats === undefined) {
    return 0;
  }

  const first_segment = selected_seats[0];
  if (
    !isNotNil(first_segment) ||
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    Object.values(first_segment)[0].length === 0
  ) {
    return 0;
  }

  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return Object.values(first_segment)[0].length;
};

export const formatSelectedSeatsToQueryParam = (
  selected_seats: SegmentSelectedSeats[]
) => {
  if (selected_seats.length === 0) {
    return [];
  }

  return selected_seats.map(segment => {
    return { [segment.segment_id]: segment.seats.map(seat => seat.id) };
  });
};

export const getPassengerSeatData = (
  selected_seats: SelectedSeatsQueryParam,
  passenger_index: number,
  trip_segments: (
    | NapiV2CartSegment
    | RelatedPublicSegment
    | RelatedPublicSegmentV4
  )[],
  translator: TranslatorService,
  vehicle_layouts: Record<string, Vehicle["layout"]>
) => {
  if (!Array.isArray(selected_seats)) {
    return "";
  }

  return selected_seats
    .map(seat => {
      const segment_id = seat && Object.keys(seat)[0];
      const segment = Object.values(trip_segments).find(
        ({ id }) => segment_id === id
      );

      if (
        !segment ||
        !seat ||
        !vehicle_layouts ||
        Object.keys(vehicle_layouts).length === 0
      ) {
        return "";
      }

      return translator.t("!checkout.passengers.seat-data", {
        class: getClassLabel(segment),
        level: getLevelLabel(seat, vehicle_layouts),
        seat: getSeatLabel(seat, passenger_index, vehicle_layouts)
      });
    })
    .filter(val => val)
    .join(" / ");
};

const getClassLabel = (
  segment: RelatedPublicSegment | RelatedPublicSegmentV4 | NapiV2CartSegment
) => {
  // TODO: UDMV4
  // @ts-expect-error fix class does not exist in v4
  return getNormalizedClassNameCapitalized(segment.class?.name || "");
};

const getLevelLabel = (
  seat: SelectedSeats,
  vehicle_layouts: Record<string, Vehicle["layout"]>
) => {
  const segment_id = Object.keys(seat)[0];
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const seat_id = Object.values(seat)[0][0];

  return (
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    getActiveFloorIndex([seat_id], vehicle_layouts[segment_id] as UDM.Floor[]) +
    1
  );
};

const getSeatLabel = (
  seat: SelectedSeats,
  passenger_index: number,
  vehicle_layouts: Record<string, Vehicle["layout"]>
) => {
  const segment_id = Object.keys(seat)[0];
  const seat_id = Object.values(seat).map(id => id[passenger_index])[0];

  const vehicle = {
    // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    layout: vehicle_layouts[segment_id]
  } as UDM.Vehicle;

  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return getSeatById(vehicle, seat_id)?.display_name;
};
